import React, { useEffect, useMemo, useState } from 'react';
import { Card, Spinner, Button } from './common';
import { QuickSummary } from './QuickSummary';
import LeadTransferPanel from './LeadTransferPanel';
import { dashboardAPI, adminAPI } from '../utils/api-service';
import type { Lead } from '../types';
import { formatCurrency, formatKarachiDateTime } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';

interface AnalyticsDashboardProps {
  isAdmin: boolean;
  showAgentTargetsOnly?: boolean;
}

interface PipelineRow {
  status: string;
  temperature: string;
  count: string | number;
}

interface AnalyticsData {
  hot_leads: string | number;
  warm_leads: string | number;
  cold_leads: string | number;
  dead_leads: string | number;
  avg_budget: string | number;
  total_agents: string | number;
}

interface AdminOverviewSummary {
  todayLeads: number;
  totalLeads: number;
  canceledLeads: number;
  canceledFollowUps: number;
  birthdaysToday: Array<{ id: string; name: string; date_of_birth: string }>;
  birthdaysTomorrow: Array<{ id: string; name: string; date_of_birth: string }>;
}

interface AdminOverviewAgent {
  id: string;
  email: string;
  name: string;
  role: string;
  last_login_at?: string | null;
  last_logout_at?: string | null;
  total_leads?: string | number;
  today_leads?: string | number;
  canceled_leads?: string | number;
  canceled_followups?: string | number;
}

interface AdminOverviewLead {
  id: string;
  client_name: string;
  email: string;
  phone: string;
  destination: string;
  status: string;
  temperature: string;
  created_at: string;
  updated_at: string;
  agent_id: string;
  agent_name: string;
  agent_email: string;
  follow_up_count?: string | number;
  canceled_followups?: string | number;
}

interface AdminOverview {
  summary: AdminOverviewSummary;
  agents: AdminOverviewAgent[];
  leads: AdminOverviewLead[];
  canceledLeads: Array<{
    id: string;
    client_name: string;
    email: string;
    phone: string;
    destination: string;
    canceled_reason?: string | null;
    canceled_at?: string | null;
    agent_name: string;
    agent_email: string;
    canceled_by_name?: string;
    canceled_by_email?: string;
  }>;
  canceledFollowUps: Array<{
    id: string;
    title: string;
    description?: string;
    canceled_reason?: string | null;
    canceled_at?: string | null;
    client_name: string;
    agent_name: string;
    agent_email: string;
    canceled_by_name?: string;
    canceled_by_email?: string;
  }>;
}

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ isAdmin, showAgentTargetsOnly }) => {
  const [pipeline, setPipeline] = useState<PipelineRow[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agents, setAgents] = useState<any[]>([]);
  const [agentStats, setAgentStats] = useState<Record<string, any>>({});
  const [agentRevenue, setAgentRevenue] = useState<Record<string, any>>({});
  const [adminOverview, setAdminOverview] = useState<AdminOverview | null>(null);
  const [newAgentOpen, setNewAgentOpen] = useState(false);
  const [newAgentEmail, setNewAgentEmail] = useState('');
  const [exportStatus, setExportStatus] = useState<string>('all');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [newAgentName, setNewAgentName] = useState('');
  const [newAgentPassword, setNewAgentPassword] = useState('');
  const [newAgentRole, setNewAgentRole] = useState('agent');
  const [selectedAgent, setSelectedAgent] = useState<any | null>(null);
  const [agentLeads, setAgentLeads] = useState<Lead[]>([]);
  const [loadingAgentLeads, setLoadingAgentLeads] = useState(false);
  const [screenshotRequestId, setScreenshotRequestId] = useState<string | null>(null);
  const [screenshotLoading, setScreenshotLoading] = useState(false);
  const [screenshotResult, setScreenshotResult] = useState<{
    requestId: string;
    agentId: string;
    screenshot?: { id: string; url: string; expires_at?: string; created_at?: string };
    error?: string;
    capturedAt?: string;
  } | null>(null);
  const { user } = useAuth();
  const [selfReportStartDate, setSelfReportStartDate] = useState('');
  const [selfReportEndDate, setSelfReportEndDate] = useState('');
  const [selfReportLoading, setSelfReportLoading] = useState(false);
  const [selfReportError, setSelfReportError] = useState('');
  const [selfReportData, setSelfReportData] = useState<{
    confirmedLeads: number;
    inProgressLeads: number;
    potentialLeads: number;
    newLeads: number;
    deadLeads: number;
    spamLeads: number;
    canceledLeads: number;
    panLeads: number;
    totalLeads: number;
    totalFollowups: number;
    completedFollowups: number;
    pastDueFollowups: number;
    activeFollowups: number;
  } | null>(null);
  const [selfReportSection, setSelfReportSection] = useState<string | null>(null);
  const [selfReportDetails, setSelfReportDetails] = useState<any[]>([]);
  const [selfReportDetailsLoading, setSelfReportDetailsLoading] = useState(false);
  const [selfReportDetailsError, setSelfReportDetailsError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError('');

        const pipelineResponse = await dashboardAPI.getPipeline();
        setPipeline(Array.isArray(pipelineResponse.data) ? pipelineResponse.data : []);

        if (isAdmin) {
          const analyticsResponse = await dashboardAPI.getAnalytics();
          setAnalytics(analyticsResponse.data || null);
        }
      } catch (err: any) {
        const message = err?.response?.status === 403
          ? 'Analytics is available for admin users only.'
          : 'Failed to load analytics data.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAdmin]);

  useEffect(() => {
    const today = new Date();
    const ninetyDaysAgo = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
    setSelfReportEndDate(today.toISOString().split('T')[0]);
    setSelfReportStartDate(ninetyDaysAgo.toISOString().split('T')[0]);
  }, []);

  const totals = useMemo(() => {
    const statusTotals: Record<string, number> = {};

    for (const row of pipeline) {
      const status = row.status || 'unknown';
      const count = Number(row.count || 0);
      statusTotals[status] = (statusTotals[status] || 0) + count;
    }

    return statusTotals;
  }, [pipeline]);

  const visiblePipeline = useMemo(() => {
    const grouped = new Map<string, PipelineRow>();

    for (const row of pipeline) {
      const status = row.status || 'unknown';
      const temperature = row.temperature || 'unknown';
      const key = `${status}::${temperature}`;
      const existing = grouped.get(key);

      if (existing) {
        existing.count = Number(existing.count || 0) + Number(row.count || 0);
      } else {
        grouped.set(key, { status, temperature, count: Number(row.count || 0) });
      }
    }

    return Array.from(grouped.values());
  }, [pipeline]);

  const refreshAgentsAndStats = async () => {
    try {
      const resp = await (adminAPI as any).getAgents();
      setAgents(Array.isArray(resp.data?.agents) ? resp.data.agents : []);
      // fetch follow-up stats
      const statsResp = await (adminAPI as any).getAgentsFollowUpStats();
      const statsArr = Array.isArray(statsResp.data?.stats) ? statsResp.data.stats : [];
      const map: Record<string, any> = {};
      for (const s of statsArr) map[s.agent_id || s.id || s.agentId] = s;
      setAgentStats(map);
      // fetch revenue stats
      const revResp = await (adminAPI as any).getAgentsRevenueStats();
      const revArr = Array.isArray(revResp.data?.stats) ? revResp.data.stats : [];
      const revMap: Record<string, any> = {};
      for (const r of revArr) revMap[r.agent_id || r.id || r.agentId] = r;
      setAgentRevenue(revMap);

      const overviewResp = await (adminAPI as any).getOverview();
      const overview = overviewResp.data;
      setAdminOverview(overview?.summary ? overview : null);
    } catch (e) {
      console.error('Failed to load agents', e);
    }
  };

  useEffect(() => {
    if (!isAdmin && !showAgentTargetsOnly) return;
    refreshAgentsAndStats();
  }, [isAdmin, showAgentTargetsOnly]);

  useEffect(() => {
    const handleScreenshotResult = (event: Event) => {
      const detail = (event as CustomEvent).detail as { requestId?: string; agentId?: string; screenshot?: { id: string; url: string; expires_at?: string; created_at?: string }; error?: string; capturedAt?: string } | undefined;
      if (!detail?.requestId) {
        return;
      }

      setScreenshotResult({
        requestId: detail.requestId,
        agentId: detail.agentId || selectedAgent?.id || '',
        screenshot: detail.screenshot,
        error: detail.error,
        capturedAt: detail.capturedAt
      });

      if (screenshotRequestId === detail.requestId) {
        setScreenshotRequestId(null);
      }
    };

    window.addEventListener('screen-capture-result', handleScreenshotResult as EventListener);
    return () => {
      window.removeEventListener('screen-capture-result', handleScreenshotResult as EventListener);
    };
  }, [screenshotRequestId, selectedAgent?.id]);

  const handleViewAgent = async (agent: any) => {
    setSelectedAgent(agent);
    setScreenshotResult(null);
    setScreenshotRequestId(null);
    setLoadingAgentLeads(true);
    try {
      const resp = await (adminAPI as any).getAgentLeads(agent.id);
      setAgentLeads(Array.isArray(resp.data?.leads) ? resp.data.leads : []);
    } catch (e) {
      console.error('Failed to load agent leads', e);
      setAgentLeads([]);
    } finally {
      setLoadingAgentLeads(false);
    }
  };

  const handleEditAgent = async (agent: any) => {
    const email = window.prompt('New email for agent', agent.email) || agent.email;
    const name = window.prompt('New name for agent', agent.name) || agent.name;
    try {
      const resp = await (adminAPI as any).updateAgent(agent.id, { email, name });
      const updated = resp.data?.agent;
      setAgents(prev => prev.map(a => (a.id === updated.id ? updated : a)));
    } catch (e) {
      console.error('Failed to update agent', e);
      alert('Failed to update agent');
    }
  };

  const handleDeleteAgent = async (agent: any) => {
    if (!confirm(`Delete agent ${agent.name || agent.email}? This cannot be undone.`)) return;
    try {
      await (adminAPI as any).deleteAgent(agent.id);
      if (selectedAgent?.id === agent.id) {
        setSelectedAgent(null);
        setAgentLeads([]);
      }
      alert('Agent deleted successfully.');
      // Refresh all agents and stats
      await refreshAgentsAndStats();
    } catch (e) {
      console.error('Failed to delete agent', e);
      alert('Failed to delete agent');
    }
  };

  const handleRequestScreenshot = async (agent: any) => {
    if (!confirm(`Request a screenshot from ${agent.name || agent.email}?`)) return;

    try {
      setScreenshotLoading(true);
      setScreenshotResult(null);
      const resp = await (adminAPI as any).requestAgentScreenshot(agent.id);
      const request = resp.data?.request;
      setScreenshotRequestId(request?.requestId || null);
      if (!request?.requestId) {
        alert('Screenshot request could not be created');
      }
    } catch (e) {
      console.error('Failed to request screenshot', e);
      alert('Failed to request screenshot');
    } finally {
      setScreenshotLoading(false);
    }
  };

  const handleExportLeads = async (type: 'xlsx' | 'txt') => {
    try {
      setExportLoading(true);
      const resp = type === 'txt'
        ? await (adminAPI as any).exportLeadsTxt(exportStatus, exportStartDate, exportEndDate)
        : await (adminAPI as any).exportLeadsSpreadsheet(exportStatus, exportStartDate, exportEndDate);
      const mime = type === 'txt' ? 'text/plain' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const ext = type === 'txt' ? 'txt' : 'xlsx';
      const blob = new Blob([resp.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tripnexus-leads-${exportStatus}-${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export leads', error);
      alert('Failed to export leads');
    } finally {
      setExportLoading(false);
    }
  };

  const handleResetPassword = async (agentId: string) => {
    if (!confirm('Generate a temporary password for this agent?')) return;
    try {
      const resp = await (adminAPI as any).resetAgentPassword(agentId);
      const temp = resp.data?.tempPassword;
      alert('Temporary password: ' + temp + '\nShare it securely with the agent.');
    } catch (e) {
      console.error('Failed to reset password', e);
      alert('Failed to reset password');
    }
  };

  const formatAgentTime = (value?: string | null) => {
    if (!value) return '—';
    try {
      return formatKarachiDateTime(value);
    } catch {
      return value;
    }
  };

  const overviewAgentsById = useMemo(() => {
    const map: Record<string, AdminOverviewAgent> = {};
    for (const agent of adminOverview?.agents || []) {
      map[agent.id] = agent;
    }
    return map;
  }, [adminOverview]);

  const handleSelfReport = async () => {
    if (!user?.id || !selfReportStartDate || !selfReportEndDate) {
      setSelfReportError('Please select a date range to view your report.');
      return;
    }

    setSelfReportLoading(true);
    setSelfReportError('');
    setSelfReportSection(null);
    setSelfReportDetails([]);

    try {
      const response = await dashboardAPI.getAgentQuickSummary(user.id, selfReportStartDate, selfReportEndDate);
      setSelfReportData(response.data || null);
    } catch (err: any) {
      setSelfReportError(err?.response?.data?.message || 'Failed to load your analytics report.');
      setSelfReportData(null);
    } finally {
      setSelfReportLoading(false);
    }
  };

  const loadSelfReportDetails = async (section: string) => {
    if (!user?.id || !selfReportStartDate || !selfReportEndDate) return;

    setSelfReportDetailsLoading(true);
    setSelfReportDetailsError('');
    setSelfReportSection(section);
    try {
      const response = await dashboardAPI.getAgentSummaryDetails(user.id, section, selfReportStartDate, selfReportEndDate);
      setSelfReportDetails(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      setSelfReportDetailsError(err?.response?.data?.message || 'Failed to load matched leads.');
      setSelfReportDetails([]);
    } finally {
      setSelfReportDetailsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Analytics</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <label className="text-sm text-slate-600 dark:text-slate-400">Export leads:</label>
            <input
              type="date"
              className="input-field w-auto"
              value={exportStartDate}
              onChange={(e) => setExportStartDate(e.target.value)}
              placeholder="Start date"
            />
            <input
              type="date"
              className="input-field w-auto"
              value={exportEndDate}
              onChange={(e) => setExportEndDate(e.target.value)}
              placeholder="End date"
            />
            <select
              className="input-field w-auto"
              value={exportStatus}
              onChange={(e) => setExportStatus(e.target.value)}
            >
              <option value="all">All</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="interested">Interested</option>
              <option value="negotiation">Negotiation</option>
              <option value="in_progress">In Progress</option>
              <option value="potential">Potential</option>
              <option value="booked">Booked</option>
              <option value="completed">Completed</option>
              <option value="canceled">Canceled</option>
              <option value="spam">Spam</option>
            </select>
            <Button size="sm" variant="secondary" onClick={() => handleExportLeads('xlsx')} loading={exportLoading}>
              Excel
            </Button>
            <Button size="sm" onClick={() => handleExportLeads('txt')} loading={exportLoading}>
              TXT
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <Card>
          <p className="text-red-600 dark:text-red-300">{error}</p>
        </Card>
      )}

      {isAdmin && agents.length > 0 && (
        <QuickSummary agents={agents} />
      )}

      {!isAdmin && (
        <Card>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-xl font-bold">My Lead Report</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Choose a date range and inspect your confirmed, in-progress, potential, and new leads.</p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div>
                <label className="block text-sm font-medium mb-1">Start date</label>
                <input type="date" value={selfReportStartDate} onChange={(e) => setSelfReportStartDate(e.target.value)} className="input-field w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">End date</label>
                <input type="date" value={selfReportEndDate} onChange={(e) => setSelfReportEndDate(e.target.value)} className="input-field w-full" />
              </div>
              <Button onClick={handleSelfReport} loading={selfReportLoading}>Generate Report</Button>
            </div>
          </div>

          {selfReportError && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
              {selfReportError}
            </div>
          )}

          {selfReportData && (
            <div className="mt-6 space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button type="button" onClick={() => loadSelfReportDetails('confirmedLeads')} className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-left dark:border-emerald-800 dark:bg-emerald-950/40">
                  <p className="text-xs text-slate-600 dark:text-slate-400">Confirmed</p>
                  <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-300">{selfReportData.confirmedLeads}</p>
                </button>
                <button type="button" onClick={() => loadSelfReportDetails('inProgressLeads')} className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-left dark:border-sky-800 dark:bg-sky-950/40">
                  <p className="text-xs text-slate-600 dark:text-slate-400">In Progress</p>
                  <p className="text-2xl font-bold text-sky-600 dark:text-sky-300">{selfReportData.inProgressLeads}</p>
                </button>
                <button type="button" onClick={() => loadSelfReportDetails('potentialLeads')} className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-left dark:border-violet-800 dark:bg-violet-950/40">
                  <p className="text-xs text-slate-600 dark:text-slate-400">Potential</p>
                  <p className="text-2xl font-bold text-violet-600 dark:text-violet-300">{selfReportData.potentialLeads}</p>
                </button>
                <button type="button" onClick={() => loadSelfReportDetails('newLeads')} className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-left dark:border-blue-800 dark:bg-blue-950/40">
                  <p className="text-xs text-slate-600 dark:text-slate-400">New</p>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-300">{selfReportData.newLeads}</p>
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button type="button" onClick={() => loadSelfReportDetails('canceledLeads')} className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-left dark:border-rose-800 dark:bg-rose-950/40">
                  <p className="text-xs text-slate-600 dark:text-slate-400">Canceled</p>
                  <p className="text-2xl font-bold text-rose-600 dark:text-rose-300">{selfReportData.canceledLeads}</p>
                </button>
                <button type="button" onClick={() => loadSelfReportDetails('deadLeads')} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-left dark:border-slate-800 dark:bg-slate-900/40">
                  <p className="text-xs text-slate-600 dark:text-slate-400">Dead</p>
                  <p className="text-2xl font-bold text-slate-600 dark:text-slate-300">{selfReportData.deadLeads}</p>
                </button>
                <button type="button" onClick={() => loadSelfReportDetails('panLeads')} className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-left dark:border-amber-800 dark:bg-amber-950/40">
                  <p className="text-xs text-slate-600 dark:text-slate-400">Pan</p>
                  <p className="text-2xl font-bold text-amber-600 dark:text-amber-300">{selfReportData.panLeads}</p>
                </button>
                <button type="button" onClick={() => loadSelfReportDetails('totalLeads')} className="rounded-lg border border-slate-300 bg-white p-3 text-left dark:border-slate-700 dark:bg-slate-800">
                  <p className="text-xs text-slate-600 dark:text-slate-400">Total Leads</p>
                  <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{selfReportData.totalLeads}</p>
                </button>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
                <div className="flex flex-wrap gap-3 text-sm text-slate-600 dark:text-slate-400">
                  <span>Follow-ups: {selfReportData.totalFollowups}</span>
                  <span>Completed: {selfReportData.completedFollowups}</span>
                  <span>Past due: {selfReportData.pastDueFollowups}</span>
                  <span>Active: {selfReportData.activeFollowups}</span>
                </div>
              </div>

              {selfReportSection && (
                <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div>
                      <h3 className="text-lg font-semibold">{selfReportSection.replace(/([A-Z])/g, ' $1')}</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Leads matching the selected category in your chosen range.</p>
                    </div>
                    <button type="button" onClick={() => setSelfReportSection(null)} className="text-sm text-slate-600 dark:text-slate-300">Close</button>
                  </div>

                  {selfReportDetailsLoading ? (
                    <p className="text-sm text-slate-600 dark:text-slate-400">Loading leads...</p>
                  ) : selfReportDetailsError ? (
                    <p className="text-sm text-red-600 dark:text-red-300">{selfReportDetailsError}</p>
                  ) : selfReportDetails.length === 0 ? (
                    <p className="text-sm text-slate-600 dark:text-slate-400">No matching leads found.</p>
                  ) : (
                    <div className="space-y-2">
                      {selfReportDetails.map((lead) => (
                        <div key={lead.id} className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <p className="font-semibold">{lead.client_name || 'Unnamed lead'}</p>
                              <p className="text-sm text-slate-500 dark:text-slate-400">{lead.phone || '—'} • {lead.destination || '—'}</p>
                            </div>
                            <div className="text-sm text-slate-500 dark:text-slate-400">
                              <span className="mr-3 capitalize">{lead.status || 'unknown'}</span>
                              <span className="capitalize">{lead.temperature || 'unknown'}</span>
                            </div>
                          </div>
                          {(lead.canceled_reason || lead.agent_remarks || lead.remarks || lead.follow_up_title || lead.follow_up_description || lead.completion_notes) && (
                            <div className="mt-2 text-sm text-slate-600 dark:text-slate-300 space-y-1">
                              {lead.canceled_reason && <p><span className="font-medium">Canceled:</span> {lead.canceled_reason}</p>}
                              {lead.agent_remarks && <p><span className="font-medium">Agent remarks:</span> {lead.agent_remarks}</p>}
                              {lead.remarks && <p><span className="font-medium">Remarks:</span> {lead.remarks}</p>}
                              {lead.follow_up_title && <p><span className="font-medium">Follow-up:</span> {lead.follow_up_title}</p>}
                              {lead.follow_up_description && <p><span className="font-medium">Follow-up notes:</span> {lead.follow_up_description}</p>}
                              {lead.completion_notes && <p className="whitespace-pre-wrap"><span className="font-medium">Completion notes:</span> {lead.completion_notes}</p>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(totals).map(([status, count]) => (
          <Card key={status}>
            <p className="text-sm text-slate-600 dark:text-slate-400 capitalize">{status} Leads</p>
            <p className="text-3xl font-bold">{count}</p>
          </Card>
        ))}

        {Object.keys(totals).length === 0 && (
          <Card>
            <p className="text-sm text-slate-600 dark:text-slate-400">Pipeline Leads</p>
            <p className="text-3xl font-bold">0</p>
          </Card>
        )}
      </div>

      <Card>
        <h2 className="text-xl font-bold mb-4">Pipeline Breakdown</h2>

        {visiblePipeline.length === 0 ? (
          <p className="text-slate-600 dark:text-slate-400">No pipeline data available yet.</p>
        ) : (
          <div className="space-y-3">
            {visiblePipeline.map((row) => (
              <div
                key={`${row.status}-${row.temperature}`}
                className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-2"
              >
                <div>
                  <p className="font-medium capitalize">{row.status}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400 capitalize">{row.temperature} temperature</p>
                </div>
                <p className="text-xl font-bold">{Number(row.count || 0)}</p>
              </div>
            ))}
          </div>
        )}
      </Card>

      {isAdmin && analytics && (
        <Card>
          <h2 className="text-xl font-bold mb-4">Global Performance (Admin)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Hot Leads</p>
              <p className="text-2xl font-bold">{Number(analytics.hot_leads || 0)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Warm Leads</p>
              <p className="text-2xl font-bold">{Number(analytics.warm_leads || 0)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Cold Leads</p>
              <p className="text-2xl font-bold">{Number(analytics.cold_leads || 0)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Dead Leads</p>
              <p className="text-2xl font-bold">{Number(analytics.dead_leads || 0)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Avg Budget</p>
              <p className="text-2xl font-bold">{formatCurrency(Number(analytics.avg_budget || 0))}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Active Agents</p>
              <p className="text-2xl font-bold">{Number(analytics.total_agents || 0)}</p>
            </div>
          </div>
        </Card>
      )}

      {isAdmin && <LeadTransferPanel />}

      {isAdmin && adminOverview && (
        <>
          {adminOverview.summary.birthdaysToday?.length > 0 && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
              <h2 className="text-lg font-bold">Employee birthdays today</h2>
              <div className="mt-2 space-y-1 text-sm">
                {adminOverview.summary.birthdaysToday.map((birthday) => <p key={birthday.id}>{birthday.name} is celebrating a birthday today.</p>)}
              </div>
            </div>
          )}
          {adminOverview.summary.birthdaysTomorrow?.length > 0 && (
            <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 p-4 text-sky-900">
              <h2 className="text-lg font-bold">Birthday reminder for tomorrow</h2>
              <div className="mt-2 space-y-1 text-sm">
                {adminOverview.summary.birthdaysTomorrow.map((birthday) => <p key={birthday.id}>{birthday.name}'s birthday is tomorrow.</p>)}
              </div>
            </div>
          )}
        </>
      )}
      {isAdmin && adminOverview && (
        <Card>
          <h2 className="text-xl font-bold mb-4">Admin Snapshot</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Today Leads</p>
              <p className="text-2xl font-bold">{adminOverview.summary.todayLeads}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Total Leads</p>
              <p className="text-2xl font-bold">{adminOverview.summary.totalLeads}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Canceled Leads</p>
              <p className="text-2xl font-bold">{adminOverview.summary.canceledLeads}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Canceled Follow-ups</p>
              <p className="text-2xl font-bold">{adminOverview.summary.canceledFollowUps}</p>
            </div>
          </div>
        </Card>
      )}

      {isAdmin && agents.length > 0 && (
        <Card>
          <h2 className="text-xl font-bold mb-4">Agent Monthly Targets</h2>
          <div className="rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-700 p-4 mb-4">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
              Complete your target or salary will be deducted accordingly.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {agents.map((a) => {
              const rev = agentRevenue[a.id] || {};
              const achieved = Number(rev.total_revenue || 0);
              const target = Number(a.monthly_target || rev.monthly_target || 5_000_000);
              const percent = target > 0 ? Math.min(100, Math.round((achieved / target) * 100)) : 0;
              return (
                <div key={a.id} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{a.name || a.email}</p>
                      <p className="text-xs text-slate-500 truncate">{a.email}</p>
                      <p className="text-xs text-slate-400 mt-1">Role: {a.role || 'agent'}</p>
                    </div>
                    <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-1 text-xs font-semibold text-slate-700 dark:text-slate-200">{a.role || 'agent'}</span>
                  </div>

                  <div className="mt-4 space-y-3">
                    <div className="grid gap-2 text-sm text-slate-600 dark:text-slate-300">
                      <div className="flex items-center justify-between">
                        <span>Total Target</span>
                        <span className="font-semibold text-slate-900 dark:text-slate-100">{formatCurrency(target)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Completed</span>
                        <span className="font-semibold text-slate-900 dark:text-slate-100">{formatCurrency(achieved)}</span>
                      </div>
                    </div>

                    <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-2.5 overflow-hidden">
                      <div className="h-2.5 rounded-full bg-emerald-500 transition-all" style={{ width: `${percent}%` }} />
                    </div>

                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>{percent}% complete</span>
                      <span>{formatCurrency(Math.max(0, target - achieved))} left</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {isAdmin && adminOverview && (
        <Card>
          <h2 className="text-xl font-bold mb-4">Canceled Leads</h2>
          {adminOverview.canceledLeads.length === 0 ? (
            <p className="text-sm text-slate-600 dark:text-slate-400">No canceled leads yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-200 dark:border-slate-700">
                    <th className="py-2 pr-4">Lead</th>
                    <th className="py-2 pr-4">Agent</th>
                    <th className="py-2 pr-4">Canceled By</th>
                    <th className="py-2 pr-4">Reason</th>
                    <th className="py-2 pr-4">Canceled At</th>
                  </tr>
                </thead>
                <tbody>
                  {adminOverview.canceledLeads.map((lead) => (
                    <tr key={lead.id} className="border-b border-slate-100 dark:border-slate-800 align-top">
                      <td className="py-3 pr-4">
                        <p className="font-medium">{lead.client_name}</p>
                        <p className="text-xs text-slate-500">{lead.email}</p>
                        <p className="text-xs text-slate-400">{lead.destination}</p>
                      </td>
                      <td className="py-3 pr-4">
                        <p className="font-medium">{lead.agent_name}</p>
                        <p className="text-xs text-slate-500">{lead.agent_email}</p>
                      </td>
                      <td className="py-3 pr-4">
                        <p className="font-medium">{lead.canceled_by_name || '—'}</p>
                        <p className="text-xs text-slate-500">{lead.canceled_by_email || '—'}</p>
                      </td>
                      <td className="py-3 pr-4 max-w-xs">{lead.canceled_reason || '—'}</td>
                      <td className="py-3 pr-4">{lead.canceled_at ? formatKarachiDateTime(lead.canceled_at) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {isAdmin && adminOverview && (
        <Card>
          <h2 className="text-xl font-bold mb-4">Canceled Follow-ups</h2>
          {adminOverview.canceledFollowUps.length === 0 ? (
            <p className="text-sm text-slate-600 dark:text-slate-400">No canceled follow-ups yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-200 dark:border-slate-700">
                    <th className="py-2 pr-4">Follow-up</th>
                    <th className="py-2 pr-4">Lead / Agent</th>
                    <th className="py-2 pr-4">Canceled By</th>
                    <th className="py-2 pr-4">Reason</th>
                    <th className="py-2 pr-4">Canceled At</th>
                  </tr>
                </thead>
                <tbody>
                  {adminOverview.canceledFollowUps.map((item) => (
                    <tr key={item.id} className="border-b border-slate-100 dark:border-slate-800 align-top">
                      <td className="py-3 pr-4">
                        <p className="font-medium">{item.title}</p>
                        <p className="text-xs text-slate-500">{item.description || '—'}</p>
                      </td>
                      <td className="py-3 pr-4">
                        <p className="font-medium">{item.client_name}</p>
                        <p className="text-xs text-slate-500">{item.agent_name}</p>
                        <p className="text-xs text-slate-400">{item.agent_email}</p>
                      </td>
                      <td className="py-3 pr-4">
                        <p className="font-medium">{item.canceled_by_name || '—'}</p>
                        <p className="text-xs text-slate-500">{item.canceled_by_email || '—'}</p>
                      </td>
                      <td className="py-3 pr-4 max-w-xs">{item.canceled_reason || '—'}</td>
                      <td className="py-3 pr-4">{item.canceled_at ? formatKarachiDateTime(item.canceled_at) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {isAdmin && (
        <Card>
          <h2 className="text-xl font-bold mb-4">Users</h2>
          <div className="flex justify-end mb-3">
            <Button size="sm" onClick={() => setNewAgentOpen(true)}>New User</Button>
          </div>
          <div className="space-y-2">
            {agents.length === 0 && <p className="text-sm text-slate-600">No agents yet.</p>}
            {agents.map((a) => (
              <div key={a.id} className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{a.name || '—'}</p>
                  <p className="text-sm text-slate-500">{a.email}</p>
                  <p className="text-xs text-slate-400">Role: {a.role || 'agent'}</p>
                  {overviewAgentsById[a.id] && (
                    <p className="text-xs text-slate-400">
                      Today: {Number(overviewAgentsById[a.id].today_leads || 0)} ·
                      Total: {Number(overviewAgentsById[a.id].total_leads || 0)} ·
                      Canceled Leads: {Number(overviewAgentsById[a.id].canceled_leads || 0)} ·
                      Canceled Follow-ups: {Number(overviewAgentsById[a.id].canceled_followups || 0)}
                    </p>
                  )}
                  {overviewAgentsById[a.id] && (
                    <p className="text-xs text-slate-400">
                      Last login: {formatAgentTime(overviewAgentsById[a.id].last_login_at)} ·
                      Last logout: {formatAgentTime(overviewAgentsById[a.id].last_logout_at)}
                    </p>
                  )}
                  {agentStats[a.id] && (
                    <p className="text-xs text-slate-400">Follow-ups: {agentStats[a.id].total} — Overdue: {agentStats[a.id].overdue}</p>
                  )}
                  {agentRevenue[a.id] && (
                    <p className="text-xs text-slate-400">Revenue: {formatCurrency(Number(agentRevenue[a.id].total_revenue || agentRevenue[a.id].total_revenue || 0))} • Bookings: {agentRevenue[a.id].bookings}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleViewAgent(a)}>View Activity</Button>
                  <Button size="sm" variant="secondary" onClick={() => handleEditAgent(a)}>Edit</Button>
                  <Button size="sm" variant="danger" onClick={() => handleDeleteAgent(a)}>Delete</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {isAdmin && adminOverview && (
        <Card>
          <h2 className="text-xl font-bold mb-4">Leads by Agent</h2>
          {adminOverview.leads.length === 0 ? (
            <p className="text-sm text-slate-600 dark:text-slate-400">No leads available yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-200 dark:border-slate-700">
                    <th className="py-2 pr-4">Lead</th>
                    <th className="py-2 pr-4">Agent</th>
                    <th className="py-2 pr-4">Destination</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Temperature</th>
                    <th className="py-2 pr-4">Created</th>
                    <th className="py-2 pr-4">Canceled Follow-ups</th>
                  </tr>
                </thead>
                <tbody>
                  {adminOverview.leads.map((lead) => (
                    <tr key={lead.id} className="border-b border-slate-100 dark:border-slate-800 align-top">
                      <td className="py-3 pr-4">
                        <p className="font-medium">{lead.client_name || '—'}</p>
                        <p className="text-xs text-slate-500">{lead.email}</p>
                        <p className="text-xs text-slate-400">{lead.phone}</p>
                      </td>
                      <td className="py-3 pr-4">
                        <p className="font-medium">{lead.agent_name || '—'}</p>
                        <p className="text-xs text-slate-500">{lead.agent_email}</p>
                      </td>
                      <td className="py-3 pr-4">{lead.destination || '—'}</td>
                      <td className="py-3 pr-4 capitalize">{lead.status}</td>
                      <td className="py-3 pr-4 capitalize">{lead.temperature}</td>
                      <td className="py-3 pr-4">{formatAgentTime(lead.created_at)}</td>
                      <td className="py-3 pr-4">{Number(lead.canceled_followups || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

        {/* New Agent modal */}
        {newAgentOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">Create New User</h3>
                <Button size="sm" onClick={() => setNewAgentOpen(false)}>Close</Button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input type="text" value={newAgentEmail} onChange={e => setNewAgentEmail(e.target.value)} className="input-field w-full" placeholder="sameer@hodophile,pk" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <input type="text" value={newAgentName} onChange={e => setNewAgentName(e.target.value)} className="input-field w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Password</label>
                  <input type="password" value={newAgentPassword} onChange={e => setNewAgentPassword(e.target.value)} className="input-field w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Role</label>
                  <select value={newAgentRole} onChange={e => setNewAgentRole(e.target.value)} className="input-field w-full">
                    <option value="agent">Agent</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setNewAgentOpen(false)}>Cancel</Button>
                  <Button size="sm" onClick={async () => {
                    if (!newAgentEmail || !newAgentPassword) { alert('Email and password required'); return; }
                    try {
                      await (adminAPI as any).createAgent({ email: newAgentEmail.trim(), name: newAgentName.trim(), password: newAgentPassword, role: newAgentRole });
                      alert('User created');
                      setNewAgentOpen(false);
                      setNewAgentEmail(''); setNewAgentName(''); setNewAgentPassword(''); setNewAgentRole('agent');
                      // refresh list
                      await refreshAgentsAndStats();
                    } catch (err) {
                      console.error('Failed to create agent', err);
                      alert('Failed to create agent');
                    }
                  }}>Create</Button>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Agent modal */}
      {selectedAgent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-3xl bg-white dark:bg-slate-900 rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold">Agent: {selectedAgent.name || selectedAgent.email}</h3>
                <p className="text-sm text-slate-500">{selectedAgent.email}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="danger" onClick={() => handleResetPassword(selectedAgent.id)}>Reset Password</Button>
                <Button size="sm" variant="secondary" onClick={() => handleRequestScreenshot(selectedAgent)} loading={screenshotLoading}>
                  Request Screenshot
                </Button>
                <Button size="sm" onClick={() => { setSelectedAgent(null); setAgentLeads([]); }}>Close</Button>
              </div>
            </div>

            {screenshotRequestId && (
              <div className="mt-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-3 text-sm text-slate-600 dark:text-slate-300">
                Waiting for screenshot from {selectedAgent.name || selectedAgent.email}...
              </div>
            )}

            {screenshotResult && screenshotResult.agentId === selectedAgent.id && (
              <div className="mt-4 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 className="font-semibold">Latest Screenshot</h4>
                    <p className="text-xs text-slate-500">
                      {screenshotResult.capturedAt ? formatKarachiDateTime(screenshotResult.capturedAt) : 'Just received'}
                    </p>
                    {screenshotResult.screenshot?.expires_at && (
                      <p className="text-xs text-slate-400">
                        Expires: {formatKarachiDateTime(screenshotResult.screenshot.expires_at)}
                      </p>
                    )}
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => setScreenshotResult(null)}>Clear</Button>
                </div>
                {screenshotResult.error ? (
                  <p className="mt-3 text-sm text-rose-600">{screenshotResult.error}</p>
                ) : (
                  <img
                    src={screenshotResult.screenshot?.url || ''}
                    alt={`Screenshot from ${selectedAgent.name || selectedAgent.email}`}
                    className="mt-3 w-full rounded-lg border border-slate-200 dark:border-slate-700"
                  />
                )}
              </div>
            )}

            <div className="mt-4">
              {loadingAgentLeads ? (
                <Spinner />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <h4 className="font-semibold">Cold Leads</h4>
                    {agentLeads.filter(l => l.temperature === 'cold').map(l => (
                      <div key={l.id} className="p-2 border rounded my-2">
                        <p className="font-medium">{l.clientName || '—'}</p>
                        <p className="text-sm text-slate-500">{l.email}</p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <h4 className="font-semibold">Potential Leads</h4>
                    {agentLeads.filter(l => l.potential).map(l => (
                      <div key={l.id} className="p-2 border rounded my-2">
                        <p className="font-medium">{l.clientName || '—'}</p>
                        <p className="text-sm text-slate-500">{l.email}</p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <h4 className="font-semibold">Confirmed Leads</h4>
                    {agentLeads.filter(l => l.pipelineStage === 'confirmed' || l.status === 'booked').map(l => (
                      <div key={l.id} className="p-2 border rounded my-2">
                        <p className="font-medium">{l.clientName || '—'}</p>
                        <p className="text-sm text-slate-500">{l.email}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
